/**
 * The facts every privacy surface states, in one place (2026-10-16).
 *
 * The direct notice a parent reads before giving consent (ConsentSheet), the
 * public privacy policy (screens/Legal.tsx) and the consent email the server
 * sends (services/api/valley/consent.py) all describe the same practices. They
 * drifted before -- the COPPA audit (C03) found the policy promising seven-day
 * logs the stack did not keep -- so the numbers live here and the server's
 * copies are checked against them by notice.test.ts.
 *
 * NOTICE_VERSION is what a parent consents to. Change what is collected or
 * why, and this version changes with consent.NOTICE_VERSION on the server;
 * dropping the old version from the server's ACCEPTED_NOTICES then asks every
 * family to consent again.
 */

export const NOTICE_VERSION = '2026-10-16';
export const NOTICE_UPDATED = '16 October 2026';

/** The retention schedule, as enforced by services/api/valley/retention.py. */
export const RETENTION = {
  draftDays: 30,
  previousDays: 7,
  newPersonDays: 30,
  deviceIdleDays: 90,
  pendingDeviceHours: 24,
  deniedDeviceDays: 30,
  consentLinkHours: 48,
  inactiveMonths: 12,
  inactiveGraceDays: 30,
  logDays: 3,
  pitrDays: 35,
  recoveryDays: 90,
  /** Monthly copies made before the schedule changed keep their old lifecycle. */
  legacyRecoveryDays: 365,
  legacyRecoveryBefore: '16 October 2026',
} as const;

/**
 * The operator, as COPPA 312.4(d)(1) requires it in the notice: name, postal
 * address, telephone number and email. Build-time values (VITE_OPERATOR_*),
 * because they are facts about whoever runs this service, and the repository
 * must not invent them. tools/audit-coppa.py refuses a production bundle
 * that is missing any of them.
 */
export interface Operator {
  name?: string | undefined;
  address?: string | undefined;
  phone?: string | undefined;
  email: string;
}

export function operator(env: Record<string, string | undefined> = import.meta.env): Operator {
  const clean = (value: string | undefined) => (value && value.trim()) || undefined;
  return {
    name: clean(env.VITE_OPERATOR_NAME),
    address: clean(env.VITE_OPERATOR_ADDRESS),
    phone: clean(env.VITE_OPERATOR_PHONE),
    email: clean(env.VITE_OPERATOR_EMAIL) ?? 'hello@birbly.app',
  };
}

export function operatorComplete(op: Operator = operator()): boolean {
  return Boolean(op.name && op.address && op.phone && op.email);
}

/** What is collected FROM a child, in the words the consent screen uses. */
export const CHILD_COLLECTION: ReadonlyArray<{ what: string; why: string }> = [
  {
    what: 'The character they design and the nickname they give it',
    why: 'So their character can live in your valley. Encrypted on your family’s devices; we cannot read it.',
  },
  {
    what: 'Money they move into or out of their own goals: the goal, the amount and the date',
    why: 'So their goals show the right amounts to them and to you.',
  },
  {
    what: 'For each device you approve for them: a random device id, the device’s public key and the last day it was used',
    why: 'So only devices you approved can open their page, and you can see which are still in use.',
  },
];

export const NOT_COLLECTED = [
  'their email address, phone number or birthday',
  'photos, voice, or location',
  'contacts or anything else on the device',
  'advertising or analytics identifiers',
] as const;
